/**
 * The opt-in camera probe — the hook that lets a real browser drive the globe.
 *
 * WHY IT EXISTS. Working out WHERE on the canvas a country is requires the live
 * camera and the live canvas rect, and neither is reachable from outside
 * `Globe.tsx`'s mount effect. Without them nothing outside a human's hands can
 * tap the globe, which is why the pinpoint path — the one interaction the game
 * is named after — went unexercised by every harness in this repo until
 * `scripts/browser-e2e.ts`. See that file's header.
 *
 * WHY IT IS SAFE. It publishes nothing unless something set
 * `__GEOGAME_GLOBE_PROBE__` to exactly `true` BEFORE the bundle ran. That is a
 * Playwright `addInitScript`, and it is not something a page can do to itself
 * after load. What it then publishes is READ-ONLY camera geometry: where the
 * camera points, how big the canvas is, and where MapLibre puts a lng/lat.
 *
 * There is no setter, no reference to the map, and nothing about the round —
 * INVARIANT 5 is untouched, because every number here is already on the
 * player's screen and derivable from a screenshot. Anything with the reach to
 * set a global before the bundle evaluates could read the whole heap anyway.
 */

/** The slice of a MapLibre map this module is allowed to see. */
export interface CameraSource {
  getCenter(): { lng: number; lat: number };
  getZoom(): number;
  getBearing(): number;
  getPitch(): number;
  isMoving(): boolean;
  getCanvas(): {
    clientWidth: number;
    clientHeight: number;
    getBoundingClientRect(): { left: number; top: number; width: number; height: number };
  };
  project(lngLat: [number, number]): { x: number; y: number };
}

export interface GlobeCameraView {
  readonly center: [number, number];
  readonly zoom: number;
  readonly bearing: number;
  readonly pitch: number;
  readonly width: number;
  readonly height: number;
  readonly rect: { left: number; top: number; width: number; height: number };
}

export interface GlobeCameraProbe {
  loaded(): boolean;
  moving(): boolean;
  /** Live camera + canvas geometry, or null before the map exists. */
  view(): GlobeCameraView | null;
  /** MapLibre's own projection, for cross-checking `projectGlobe`. */
  project(lng: number, lat: number): [number, number] | null;
}

export interface ProbeHost {
  __GEOGAME_GLOBE_PROBE__?: unknown;
  __GEOGAME_GLOBE__?: GlobeCameraProbe | undefined;
}

/** The global object, but only when it has explicitly asked for a probe. */
function enabledHost(scope: object = globalThis): ProbeHost | null {
  const host = scope as ProbeHost;
  return host.__GEOGAME_GLOBE_PROBE__ === true ? host : null;
}

/**
 * Publish the probe, if and only if this page opted in. `source` is read
 * lazily on every call, so the probe reflects the live camera rather than a
 * snapshot taken at load.
 */
export function publishCameraProbe(
  source: { map(): CameraSource | null; loaded(): boolean },
  scope: object = globalThis,
): boolean {
  const host = enabledHost(scope);
  if (!host) return false;
  host.__GEOGAME_GLOBE__ = {
    loaded: () => source.loaded(),
    moving: () => source.map()?.isMoving() ?? false,
    view: () => {
      const map = source.map();
      if (!map) return null;
      const c = map.getCenter();
      const canvas = map.getCanvas();
      const r = canvas.getBoundingClientRect();
      return {
        center: [c.lng, c.lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
        width: canvas.clientWidth,
        height: canvas.clientHeight,
        rect: { left: r.left, top: r.top, width: r.width, height: r.height },
      };
    },
    project: (lng, lat) => {
      const map = source.map();
      if (!map) return null;
      const p = map.project([lng, lat]);
      return [p.x, p.y];
    },
  };
  return true;
}

/** Withdraw the probe when the map goes away, so a stale one cannot be read. */
export function retractCameraProbe(scope: object = globalThis): void {
  const host = enabledHost(scope);
  if (host) host.__GEOGAME_GLOBE__ = undefined;
}
